import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';
import ChatMessageBubble from './ChatMessageBubble';
import './TutorChatDrawer.css';

export default function TutorChatDrawer({
  studentName,
  messages,
  onSend,
  onRead,
  historyCount,
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [historyVisible, setHistoryVisible] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    onRead?.();
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    if ((historyCount ?? 0) > 0) {
      setHistoryVisible(true);
      const t = setTimeout(() => setHistoryVisible(false), 6000);
      return () => clearTimeout(t);
    }
    setHistoryVisible(false);
    return undefined;
  }, [open, historyCount, onRead]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, messages?.length]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    onSend?.(text);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`tutor-chat-drawer ${open ? 'tutor-chat-drawer--open' : ''}`}>
      <button
        type="button"
        className="tutor-chat-drawer__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {open && (
        <div className="tutor-chat-drawer__inner">
          <div className="tutor-chat-drawer__header">Chat — {studentName}</div>
          <div className="tutor-chat-drawer__messages">
            {historyVisible && (historyCount ?? 0) > 0 && (
              <div className="tutor-chat-drawer__history-banner">
                {historyCount} previous message{(historyCount ?? 0) > 1 ? 's' : ''}
              </div>
            )}
            {(messages ?? []).map((msg, i) => (
              <ChatMessageBubble
                key={msg._id ?? `${msg.timestamp ?? i}-${i}`}
                message={msg.message}
                timestamp={msg.timestamp ?? msg.createdAt}
                isOwn={msg.senderType === 'tutor'}
              />
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="tutor-chat-drawer__input-row">
            <textarea
              className="tutor-chat-drawer__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message student..."
              maxLength={500}
              rows={1}
            />
            <button
              type="button"
              className="tutor-chat-drawer__send"
              onClick={handleSend}
              disabled={!input.trim()}
              aria-label="Send message"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
